import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { AdminAlert, AdminComingSoon, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import {
  adminGetOfferwallCompletions,
  adminGetOfferwallRevenue,
  adminFlagCompletion,
  adminVoidCompletion,
} from '../../../services/admin'

const PROVIDER_FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'cpx', label: 'CPX Research' },
  { key: 'adgem', label: 'AdGem' },
]

const FLAGGED_FILTERS = [
  { key: undefined, label: 'All' },
  { key: false, label: 'Unflagged' },
  { key: true, label: 'Flagged' },
]

function fmtAmount(amount) {
  const n = Number(amount)
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : amount ?? '—'
}

function RevenueCard({ label, value, accent }) {
  return (
    <div className="border-4 border-on-background bg-surface-container-low p-5 flex flex-col gap-1"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant">{label}</p>
      <p className={`font-extrabold text-[26px] leading-none ${accent ? 'text-primary-container' : ''}`}>
        {value}
      </p>
    </div>
  )
}

function StatusBadge({ status, voided }) {
  if (voided) {
    return <span className="px-2 py-0.5 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest">Voided</span>
  }
  const s = (status ?? '').toLowerCase()
  const style = s === 'completed'
    ? 'border-primary-container text-on-primary-container bg-primary-container/15'
    : 'border-on-background/30 text-on-surface-variant'
  return <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${style}`}>{status ?? 'unknown'}</span>
}

function ConfirmDialog({ title, children, confirmLabel, danger, loading, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-4 p-6"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">{title}</h2>
          <button onClick={onCancel} className="p-1 hover:bg-surface-container transition-colors">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
        {children}
        <div className="flex gap-2 mt-1">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 px-4 py-2.5 border-2 font-bold text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 ${
              danger ? 'border-error bg-error text-on-error hover:opacity-90' : 'border-primary-container bg-primary-container text-on-primary-container hover:brightness-105'
            }`}>
            {loading ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export function OfferwallManagement() {
  const { hasPermission } = useAuth()
  const canEdit = hasPermission('marketplace')
  const [provider, setProvider] = useState(undefined)
  const [flagged, setFlagged] = useState(undefined)

  const [completions, setCompletions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [revenue, setRevenue] = useState(null)
  const [revenueLoading, setRevenueLoading] = useState(true)
  const [revenueError, setRevenueError] = useState(null)

  const [flagTarget, setFlagTarget] = useState(null) // completion row
  const [flagReason, setFlagReason] = useState('')
  const [flagError, setFlagError] = useState(null)
  const [voidTarget, setVoidTarget] = useState(null) // completion row
  const [actingOn, setActingOn] = useState(null) // id currently being flagged/voided
  const [actionError, setActionError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const loadCompletions = useCallback(() => {
    setLoading(true)
    setError(null)
    adminGetOfferwallCompletions({ provider, flagged })
      .then(setCompletions)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [provider, flagged])

  const loadRevenue = useCallback(() => {
    setRevenueLoading(true)
    setRevenueError(null)
    adminGetOfferwallRevenue()
      .then(setRevenue)
      .catch(setRevenueError)
      .finally(() => setRevenueLoading(false))
  }, [])

  useEffect(() => { loadCompletions() }, [loadCompletions])
  useEffect(() => { loadRevenue() }, [loadRevenue])
  if (error && isNotBuiltYet(error) && completions.length === 0 && !loading) {
    return <AdminComingSoon label="Offerwall Management" />
  }

  const openFlag = (row) => { setFlagTarget(row); setFlagReason(''); setFlagError(null) }
  const closeFlag = () => { setFlagTarget(null); setFlagError(null) }

  const confirmFlag = async () => {
    if (!flagReason.trim()) { setFlagError('A reason is required'); return }
    setActingOn(flagTarget.id)
    setFlagError(null)
    setActionError(null)
    try {
      await adminFlagCompletion(flagTarget.provider, flagTarget.id, flagReason.trim())
      setSuccessMsg('Completion flagged for review.')
      setTimeout(() => setSuccessMsg(null), 3000)
      closeFlag()
      loadCompletions()
    } catch (err) {
      setFlagError(err.message ?? 'Failed to flag completion')
    } finally {
      setActingOn(null)
    }
  }

  const confirmVoid = async () => {
    setActingOn(voidTarget.id)
    setActionError(null)
    try {
      await adminVoidCompletion(voidTarget.provider, voidTarget.id)
      setSuccessMsg('Completion voided and the reward was reversed.')
      setTimeout(() => setSuccessMsg(null), 3000)
      setVoidTarget(null)
      loadCompletions()
      loadRevenue()
    } catch (err) {
      setActionError(err.message ?? 'Failed to void completion')
      setVoidTarget(null)
    } finally {
      setActingOn(null)
    }
  }

  return (
    <div className="flex flex-col gap-8">

      {/* ── Flag dialog ── */}
      {flagTarget && (
        <ConfirmDialog
          title="Flag Completion"
          confirmLabel="Flag for Review"
          loading={actingOn === flagTarget.id}
          onConfirm={confirmFlag}
          onCancel={closeFlag}
        >
          <p className="text-[12px] text-on-surface-variant">
            Provider: <span className="font-bold text-on-surface uppercase">{flagTarget.provider}</span>
            {' · '}Amount: <span className="font-bold text-on-surface">{fmtAmount(flagTarget.amount)}</span>
          </p>
          {flagError && <AdminAlert type="error" message={flagError} />}
          <div className="flex flex-col gap-1">
            <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Reason</label>
            <textarea
              rows={3}
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              placeholder="e.g. Suspicious completion pattern, possible fraud"
              className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container resize-y"
            />
          </div>
        </ConfirmDialog>
      )}

      {/* ── Void dialog ── */}
      {voidTarget && (
        <ConfirmDialog
          title="Void Completion"
          confirmLabel="Void & Reverse Reward"
          danger
          loading={actingOn === voidTarget.id}
          onConfirm={confirmVoid}
          onCancel={() => setVoidTarget(null)}
        >
          <p className="text-[12px] text-on-surface-variant">
            This permanently marks the completion as voided and reverses the <span className="font-bold text-on-surface">{fmtAmount(voidTarget.amount)}</span> reward already credited to the user. This cannot be undone.
          </p>
        </ConfirmDialog>
      )}

      {/* ── Revenue summary ── */}
      <div>
        <h2 className="font-extrabold text-[18px] uppercase tracking-widest mb-1">Offerwall Revenue</h2>
        <p className="text-[12px] text-on-surface-variant mb-4">Total value of completed, non-voided offers across both providers.</p>
        {revenueError && !isNotBuiltYet(revenueError) && (
          <AdminAlert type="error" message={revenueError.message ?? 'Failed to load revenue'} />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {revenueLoading ? (
            <>
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </>
          ) : (
            <>
              <RevenueCard label="Total Revenue" value={fmtAmount(revenue?.total)} accent />
              <RevenueCard label="CPX Research" value={fmtAmount(revenue?.cpx)} />
              <RevenueCard label="AdGem" value={fmtAmount(revenue?.adgem)} />
            </>
          )}
        </div>
      </div>

      {/* ── Completions list ── */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-extrabold text-[18px] uppercase tracking-widest">Completions</h2>
          <button onClick={loadCompletions} disabled={loading}
            className="px-4 py-2 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-primary-container hover:text-on-primary-container transition-colors disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Provider</span>
            <div className="flex gap-1 border-2 border-on-background w-fit">
              {PROVIDER_FILTERS.map(f => (
                <button key={f.label} onClick={() => setProvider(f.key)}
                  className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                    provider === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">Status</span>
            <div className="flex gap-1 border-2 border-on-background w-fit">
              {FLAGGED_FILTERS.map(f => (
                <button key={f.label} onClick={() => setFlagged(f.key)}
                  className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                    flagged === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                  }`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {actionError && <AdminAlert type="error" message={actionError} />}
        {successMsg && <AdminAlert type="success" message={successMsg} />}
        {error && !isNotBuiltYet(error) && (
          <AdminAlert type="error" message={error.message ?? 'Failed to load completions'} />
        )}

        {loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full rounded" />)}
          </div>
        ) : completions.length === 0 ? (
          <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
            <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No completions match these filters</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {completions.map(row => (
              <div key={`${row.provider}-${row.id}`}
                className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
                style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">
                      {row.provider}
                    </span>
                    <StatusBadge status={row.status} voided={row.voided} />
                    {row.flagged && (
                      <span className="px-2 py-0.5 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest">Flagged</span>
                    )}
                  </div>
                  <p className="font-bold text-[14px]">{fmtAmount(row.amount)}</p>
                  <p className="text-[11px] text-on-surface-variant font-mono truncate">User: {row.profileId ?? '—'}</p>
                  {row.campaignId && (
                    <p className="text-[11px] text-on-surface-variant font-mono truncate">Campaign: {row.campaignId}</p>
                  )}
                  {row.flagReason && (
                    <p className="text-[11px] text-error mt-1">Reason: {row.flagReason}</p>
                  )}
                  <p className="text-[11px] text-on-surface-variant font-mono mt-1">
                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                  </p>
                </div>
                {canEdit && <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openFlag(row)}
                    disabled={row.voided || actingOn === row.id}
                    className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-40"
                  >
                    Flag
                  </button>
                  <button
                    onClick={() => setVoidTarget(row)}
                    disabled={row.voided || actingOn === row.id}
                    className="px-3 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-40"
                  >
                    Void
                  </button>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}